<script setup lang="ts">
import type { PdpEcommerceBlockProductDetailsProps } from '@design-system/components/Pdp/EcommerceBlock/Product/PdpEcommerceBlockProductDetails.props'
import type { ProductCardHorizontal } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import type { TemplatesPdpProps } from './TemplatesPdp.props'
import {
  PDP_SIMILAR_PRODUCTS_ID,
  PDP_YOU_MAY_ALSO_LIKE_ID,
} from '@integration-layer/data/xGenIds'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import PdpMessageSideSlide from '@design-system/components/Pdp/EcommerceBlock/PdpMessageSideSlide.vue'
import PdpVirtualMirrorModal from '@design-system/components/Pdp/EcommerceBlock/PdpVirtualMirrorModal.vue'
import type { VirtualMirrorProductData } from '@design-system/components/Pdp/EcommerceBlock/PdpVirtualMirrorModal.props'
import { useShowHeaderSearch } from '@design-system/composables/useShowHeaderSearch'

const props = defineProps<TemplatesPdpProps>()
const emit = defineEmits<{
  'update:selectedSizeIndex': [selectedSizeIndex: number | null]
  'update:selectedCountry': [selectedCountry: string]
  'update:selectedColor': [selectedColor: string]
  'update:selectedExtSku': [selectedExtSku: string]
  'update-wishlist': []
  addToCart: []
}>()

const { openDialog } = useDialog()
const { addProductToRecentlyViewedCookie: addProductToRecentlyViewed } =
  useRecentlyViewed('recently-viewed')

const { fetchAndMapProductData } = useTemplatePdp()
const { addToCartPayload } = useAddToCart()

const {
  getProductsRecentlyViewed: getPerBrandProductsRecentlyViewed,
  addProductToRecentlyViewedCookie: addPerBrandProductToRecentlyViewed,
} = useRecentlyViewed(`${props.brand}-recently-viewed`)

const { data: recentlyViewedProducts } =
  await getPerBrandProductsRecentlyViewed(30, 0, [props.productId], {
    lazy: true,
    server: false,
  })

const { isShoppable } = useIsShoppable()
const productDetailsBlockElement = ref<HTMLElement>()
const breadcrumbsTopDistance = ref('')

const nuxtApp = useNuxtApp()
const hideForCustomers = ref(false)
onMounted(async () => {
  const { hideForCustomers: hideForCustomersValue } =
    await nuxtApp.runWithContext(() =>
      useHideForCustomerTags('user-tags-eligibility')
    )
  hideForCustomers.value = hideForCustomersValue
})

/* NOTE: const height below is taking a fallback the same height value used in desktop-breadcrumbs scss class ( 600px ),
which is choosen as approxymately default value for the element's height */

const setBreadcrumbsTopDistance = () => {
  const height = productDetailsBlockElement.value?.clientHeight ?? 600
  breadcrumbsTopDistance.value = `top: max(calc(100dvh - 3rem), calc(${height}px + 4.5rem))`
}

useResizeObserver(productDetailsBlockElement, () => {
  setBreadcrumbsTopDistance()
})

onMounted(() => {
  scrollIntoPageView({
    scrollBothStrips: true,
    dynamicScrollPlp: false,
  })

  const updateIsScrolled = useThrottleFn(() => {
    const zeroIndexHeight =
      removePx(stripMaxHeight.value) + removePx(monogramMaxHeight.value)
    isScrolled.value = window.scrollY > zeroIndexHeight && !isLarge.value
  }, 100)
  window.addEventListener('scroll', updateIsScrolled, { passive: true })

  const deferNonCriticalWork = () => {
    const scrollData = JSON.parse(localStorage.getItem('scrollYVal') ?? '{}')
    scrollData.prodPage = true
    scrollData.scroll = `productTile-${props.productId}`
    localStorage.setItem('scrollYVal', JSON.stringify(scrollData))
    addProductToRecentlyViewed(props.productId)
    addPerBrandProductToRecentlyViewed(props.productId)
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(deferNonCriticalWork)
  } else {
    setTimeout(deferNonCriticalWork, 1)
  }

  onUnmounted(() => {
    window.removeEventListener('scroll', updateIsScrolled)
  })
})

const { breadcrumbs } = useBreadcrumbs({
  dataOrigin: 'algolia',
  currentPageTitle: props.title,
  productCategories: props.mainCategory,
})

const linkToMainCategory = computed(() => {
  const arrBc = breadcrumbs.value
  if (!arrBc?.length || !props.mainCategory?.length) return undefined
  const lastCategoryName = props.mainCategory.at(-1)?.name?.toLowerCase()
  if (!lastCategoryName) return undefined
  return arrBc.find(item => item?.label.toLowerCase() === lastCategoryName)?.url
})

const oneSize = computed(() => {
  return (
    props.sizes?.[props.selectedCountry]?.[0]?.['size'] === 'TU' ||
    props.isSingleSizeByMicroCat
  )
})

const { breakpoint } = useProjectBreakpoint()
const isLarge = breakpoint.greaterOrEqual('lg')
const isMobile = breakpoint.smaller('md')

const isSliderNeeded = (items: number) =>
  isLarge.value ? items > 5 : items > 2

// EIFE-3600: temporary XGen similar products, shown only with ?xgenSimilar=true.
const route = useRoute()
const isXgenSimilarProducts = computed(() => route.query.xgenSimilar === 'true')

const stripMaxHeight = useCssVar('--marketing-strip-max-height')
const monogramMaxHeight = useCssVar('--monogram-strip-height')

const isScrolled = ref(false)

const handleOpenSidebar = (sidebarId: string) => openDialog(sidebarId)

const handleSimilarProductsEvent = () => {
  const id = 'similarProducts'
  scrollToAnchor(document.getElementById(id) ? id : 'youMayAlsoLike')
}

const scrollToAnchor = (id: string) => {
  const element = document.getElementById(id)
  if (!element) return
  const elementOffsetTop = element.getBoundingClientRect().top
  if (!isMobile.value) {
    const headerMonogram = document.getElementById('header-monogram')
    if (headerMonogram) {
      let headerHeight = headerMonogram.clientHeight
      const stripsContainer = document.getElementById('strips-container')
      if (stripsContainer) {
        headerHeight = stripsContainer.clientHeight
      }
      const scrollY = window.scrollY
      if (scrollY < headerHeight) {
        window.scrollTo({
          top: elementOffsetTop - (headerHeight - 20),
          behavior: 'instant',
        })
      } else if (scrollY === headerHeight) {
        window.scrollTo({ top: elementOffsetTop + 20, behavior: 'instant' })
      } else {
        element.scrollIntoView({ behavior: 'instant' })
      }
    }
  } else {
    window.scrollBy({ top: elementOffsetTop - 30, behavior: 'instant' })
  }
}

const productDetailsProps = computed(
  (): PdpEcommerceBlockProductDetailsProps => ({
    ...props,
    breadcrumbs: breadcrumbs.value ?? [], // ?? prop not being used inside PdpEcommerceBlockProductDetails
    isShoppable: isShoppable.value,
  })
)

const swiperRef = ref(null)
const completeTheLookSection = ref(null)

/**
 * Quick Buy Popup
 */
const { openQuickBuySlide } = useUIQuickBuyPopUpSlide()

const openQuickBuyPopUpLayer = (product: ProductCardHorizontal) => {
  openQuickBuySlide({
    product,
    isQuickBuyPlp: true,
  })
}

const breadcrumbLabel = computed(() => {
  if (!breadcrumbs.value || breadcrumbs.value.length - 2 < 0) return ''
  return breadcrumbs.value.at(-2)?.label
})

/* Pdp 3D image */
const { openDialog: open3dImageDialog } = useDialog(SIDEBAR_IDS.pdp3dImage)
const { openDialog: openVirtualMirrorDialog } = useDialog(
  SIDEBAR_IDS.pdpVirtualMirrorModal
)
const { dispatchActionVirtualMirrorEvent } = useGAActionEvents()
const { ts } = useI18n()

const virtualMirrorMobileLabel = computed(() =>
  ts('pdp.virtualMirror.mobileLabel')
)
const showVirtualMirrorMobileLabel = computed(
  () =>
    virtualMirrorMobileLabel.value &&
    !virtualMirrorMobileLabel.value.startsWith('pdp.')
)

const openVirtualMirror = (eventDetail: string) => {
  dispatchActionVirtualMirrorEvent(
    'open_vto',
    eventDetail,
    props.productId,
    props.title
  )
  openVirtualMirrorDialog()
}
const product3dImage = computed(() => {
  return props.orderedGallery.find(item => item.type === 'TResponsiveImage3D')
})

const getProductVirtualMirror = async (
  productToLoad: string
): Promise<VirtualMirrorProductData | null> => {
  if (!productToLoad?.trim()) return null
  const productData = await fetchAndMapProductData(productToLoad)
  if (!productData) return null

  const skuCode = productData.computedLists.skuList?.[0]
  const sizeLabel = productData.computedLists.sizeList?.[0]?.size ?? ''
  const firstUpc = skuCode
    ? (Object.values(productData.upcs ?? {})[0] as string | undefined)
    : null

  addToCartPayload.value = {
    name: productData.title,
    sku_code: skuCode ?? '',
    quantity: 1,
    metadata: {
      size: `${sizeLabel} (${props.selectedCountry})`,
      color: productData.selectedColor,
      queryID: productToLoad,
    },
  }

  return {
    upc: firstUpc ?? null,
    title: productData.title,
    price: productData.price,
    selectedColor: productData.selectedColor,
  }
}

const { ...virtualMirror } = useVirtualMirror()
const { showHeaderSearch } = useShowHeaderSearch()
</script>

<template>
  <div>
    <PdpEcommerceBlockContainer
      id="pdp-main-block"
      class="carousel-container-overflowed"
      :class="{ giorgioArmaniHeaderPadding: offsetTop }"
      :hide-mobile-topbar="isScrolled"
    >
      <template #top-bar>
        <UtilsGoBack :to="linkToMainCategory">
          <template #default>
            {{ breadcrumbLabel }}
          </template>
        </UtilsGoBack>
        <div class="flex items-center gap-2">
          <!-- Virtual Mirror button (sunglasses only) -->
          <button
            v-if="hasVirtualMirror"
            class="text-book-8 border-primitives-grey-100 bg-neutral-white flex h-10 items-center gap-2 border p-2 uppercase tracking-wider"
            @click="openVirtualMirror('product_image')"
          >
            <DefaultIconsVirtualMirror
              class="h-6 w-6"
              :aria-label="$ts('aria-labels.virtualMirror')"
            />
            <span v-if="showVirtualMirrorMobileLabel">{{
              virtualMirrorMobileLabel
            }}</span>
          </button>
          <button
            v-if="product3dImage"
            class="border-primitives-grey-100 bg-neutral-white h-10 border p-2"
            :aria-label="$ts('aria-labels.3dImage')"
            @click="open3dImageDialog()"
          >
            <DefaultIcons3DNew class="h-6 w-6" :aria-hidden="true" />
          </button>
        </div>
      </template>
      <template #left-side-pdp>
        <!-- NOTE: in :class => h-[calc(100% - 18px (current breadcrumbs height) - 24px (padding))]
                   Added pb-md and -mb-md classes ( 24px, relied on the padding used on the calc above )
                   to maintain the space from breadcrumbs
        -->
        <div class="lg:flex lg:h-[calc(100%-42px)] lg:flex-col">
          <div
            ref="productDetailsBlockElement"
            class="pb-md -mb-md lg:sticky lg:top-[--header-default-height]"
          >
            <div class="lg:min-h-[470px]">
              <!-- :style="{
                  marginTop: `${Math.min(y, swiperHeight + 200) - 50}px`,
                }" -->
              <PdpEcommerceBlockProductDetails
                is-heading
                class="lg:!mt-0"
                :hide-for-customers="hideForCustomers"
                v-bind="productDetailsProps"
                :main-category="mainCategory"
                :selected-ext-sku="selectedExtSku"
                :size-grid-code="sizeGridCode"
                :is-fallback="isFallback"
                :this-product-dims="thisProductDims"
                @update:selected-size-index="
                  emit('update:selectedSizeIndex', $event)
                "
                @update:selected-country="
                  emit('update:selectedCountry', $event)
                "
                @update:selected-ext-sku="emit('update:selectedExtSku', $event)"
                @open-sidebar="handleOpenSidebar"
                @similar-products="handleSimilarProductsEvent()"
                @add-to-cart="emit('addToCart')"
              />
            </div>
          </div>
          <!-- NOTE: in :class => lg:-mb-[ 18px (breadcrumbs height) + 24px (padding) ] -->
          <div
            class="breadcrumbs-desktop lg:-mb-[42px]"
            :style="breadcrumbsTopDistance"
          >
            <UIBreadcrumbs
              class="col-span-8 hidden max-w-full lg:block"
              :items="breadcrumbs ?? []"
              pdp
            />
          </div>
        </div>
      </template>
      <template #right-side-pdp>
        <div
          ref="swiperRef"
          class="bg-primitives-off-white top-[var(--header-default-height)] col-span-12 shadow lg:sticky lg:top-0 lg:order-2 lg:col-start-4 lg:col-end-10"
        >
          <!-- Main product gallery -->
          <PdpEcommerceBlockGallerySlider
            :gallery="orderedGallery"
            :title="title"
            :price="price"
            :is-complete-the-look-available="!!completeTheLookSection"
            :has-virtual-mirror="hasVirtualMirror"
            @complete-the-look="scrollToAnchor('completeTheLook')"
            @open-virtual-mirror="openVirtualMirror('product_image')"
          />
          <PdpEcommerceBlockProductName
            class="mt-3.5 lg:hidden"
            :title="title"
            :price="price"
            :old-price="oldPrice"
            :discount="discount"
            :tags="tags"
            :is-shoppable="isShoppable && !infoCommerce"
          />
        </div>
        <div
          class="order-3 col-span-full lg:col-start-10 lg:col-end-13 lg:flex lg:h-full lg:flex-col"
        >
          <!-- Small right gallery with product details  -->
          <div class="lg:sticky lg:top-[--header-default-height] lg:mb-5">
            <PdpEcommerceBlockProductInfo
              :product-code="productId"
              :gallery="orderedGallery"
              :product-details="productDetails"
              :product-care="productCare"
              :editorial-product-focus="editorialProductFocus"
              :product-focus="productFocus"
              :has-virtual-mirror="hasVirtualMirror"
              @update-wishlist="emit('update-wishlist')"
              @complete-the-look="scrollToAnchor('completeTheLook')"
              @open-virtual-mirror="openVirtualMirror($event)"
            />
          </div>
          <div
            v-if="completeTheLookSection"
            class="hidden lg:flex lg:flex-grow"
          >
            <div
              class="complete-the-look-button lg:sticky lg:bottom-8 lg:mt-auto"
            >
              <button
                class="lg:gap-xxs order-3 flex items-center"
                @click="scrollToAnchor('completeTheLook')"
              >
                <span class="text-book-6 uppercase">
                  {{
                    $ts('pdp.productDetails.informationSection.completeTheLook')
                  }}
                </span>
                <DefaultIconsDown class="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </template>
    </PdpEcommerceBlockContainer>

    <DelayHydration>
      <VisenzeRecommendations type="STL" :product-sku="productId">
        <template #default="{ products, bestImage }">
          <LazyPdpCompleteLook
            v-if="products && products.length && showHeaderSearch"
            id="completeTheLook"
            ref="completeTheLookSection"
            :title="$ts('pdp.relatedProducts.completeTheLook')"
          >
            <template #product>
              <div
                class="cmtl-container flex min-h-full flex-col lg:pr-[--padding-x]"
              >
                <NuxtPicture
                  provider="cloudinary"
                  :src="bestImage ?? (orderedGallery[0].data as string)"
                  :placeholder="[67, 85]"
                  :alt="title"
                  sizes="sm:100vw lg:20vw"
                  class="aspect-pdp-image w-full"
                  :img-attrs="{
                    class: 'h-full w-full object-contain',
                  }"
                  :modifiers="{ aspectRatio: '4:5' }"
                  quality="70"
                  loading="lazy"
                  fit="fill"
                />
                <PdpEcommerceBlockProductDetails
                  class="!py-0 lg:mt-8"
                  hide-details
                  :hide-for-customers="hideForCustomers"
                  v-bind="{ ...productDetailsProps, tags: [] }"
                  :selected-ext-sku="selectedExtSku"
                  :this-product-dims="thisProductDims"
                  @update:selected-size-index="
                    emit('update:selectedSizeIndex', $event)
                  "
                  @update:selected-country="
                    emit('update:selectedCountry', $event)
                  "
                  @update:selected-ext-sku="
                    emit('update:selectedExtSku', $event)
                  "
                  @open-sidebar="handleOpenSidebar"
                  @add-to-cart="emit('addToCart')"
                />
              </div>
            </template>
            <template #slider>
              <UISliderWithArrows
                v-if="products && products.length"
                :items="products"
                :loop="false"
                :slide-custom-width="`${products.length > 1 ? 'w-1/2 lg:w-[calc((100%_-_var(--padding-x))_/_2)]' : 'w-full'} lg:!w-[calc((100%)_/_3)]`"
                :show-scrollbar="products.length > 3"
                :right-window-edge="products.length > 3"
                :show-navigation-prev-arrow="false"
                :has-slide-vertical-border="true"
                :hide-lateral-slides-on-mobile="true"
                :class="{
                  'lg:overflow-hidden': products.length <= 3,
                }"
              >
                <template #default="item">
                  <WrapperProductTile :product="item">
                    <template #default="{ product, productCardHorizontal }">
                      <UIProductTile
                        :disable-swipe-mobile="true"
                        :product="product"
                        @open-quick-buy="
                          () =>
                            productCardHorizontal &&
                            openQuickBuyPopUpLayer(productCardHorizontal)
                        "
                      />
                    </template>
                  </WrapperProductTile>
                </template>
              </UISliderWithArrows>
            </template>
          </LazyPdpCompleteLook>
        </template>
      </VisenzeRecommendations>
    </DelayHydration>

    <DelayHydration>
      <XGenRecommendations
        v-if="isShoppable && isXgenSimilarProducts"
        :prediction="PDP_SIMILAR_PRODUCTS_ID"
        :initial-cl-fetch="10"
        tracking-name="similar_products"
      >
        <template #default="{ products }">
          <LazyPdpRelatedProducts
            v-if="products && products.length"
            id="similarProducts"
            :title="$ts('pdp.relatedProducts.similarProducts')"
            class="[&_>div]:!pt-12 md:[&_>div]:!py-12 xl:[&_>div]:!py-16"
          >
            <UISliderWithArrows
              :id="`XSE-${PDP_SIMILAR_PRODUCTS_ID}`"
              :items="products"
              :loop="isSliderNeeded(products.length)"
              :show-navigation="isSliderNeeded(products.length)"
              class="XGen_SmartElement"
              has-slide-vertical-border
              :style="isSliderNeeded(products.length) ? '' : '--padding-x: 1px'"
              slide-custom-width="w-[calc((100%_-_var(--padding-x)_*_2)_/_2)] lg:w-[calc((100%_-_var(--padding-x)_*_2)_/_5)]"
            >
              <template #default="item">
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      xse-type="rec"
                      :xse-prod-code="product.sku"
                      :disable-swipe-mobile="true"
                      :product="{
                        ...product,
                        linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${PDP_SIMILAR_PRODUCTS_ID}`,
                      }"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </template>
            </UISliderWithArrows>
          </LazyPdpRelatedProducts>
        </template>
      </XGenRecommendations>
      <VisenzeRecommendations
        v-else-if="isShoppable"
        type="VSR"
        :product-sku="productId"
      >
        <template #default="{ products }">
          <LazyPdpRelatedProducts
            v-if="products && products.length"
            id="similarProducts"
            :title="$ts('pdp.relatedProducts.similarProducts')"
            class="[&_>div]:!pt-12 md:[&_>div]:!py-12 xl:[&_>div]:!py-16"
          >
            <UISliderWithArrows
              :items="products"
              :loop="isSliderNeeded(products.length)"
              :show-navigation="isSliderNeeded(products.length)"
              has-slide-vertical-border
              :style="isSliderNeeded(products.length) ? '' : '--padding-x: 1px'"
              slide-custom-width="w-[calc((100%_-_var(--padding-x)_*_2)_/_2)] lg:w-[calc((100%_-_var(--padding-x)_*_2)_/_5)]"
            >
              <template #default="item">
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      :disable-swipe-mobile="true"
                      :product="product"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </template>
            </UISliderWithArrows>
          </LazyPdpRelatedProducts>
        </template>
      </VisenzeRecommendations>
    </DelayHydration>

    <DelayHydration>
      <XGenRecommendations
        :prediction="PDP_YOU_MAY_ALSO_LIKE_ID"
        :initial-cl-fetch="10"
        tracking-name="products_you_may_like"
      >
        <template #default="{ products }">
          <LazyPdpRelatedProducts
            v-if="products && products.length"
            id="youMayAlsoLike"
            :title="$ts('pdp.relatedProducts.youMayAlsoLike')"
            class="[&_>div]:!pt-12 md:[&_>div]:!py-12 xl:[&_>div]:!py-16"
          >
            <UISliderWithArrows
              :id="`XSE-${PDP_YOU_MAY_ALSO_LIKE_ID}`"
              :items="products"
              class="XGen_SmartElement"
              has-slide-vertical-border
              slide-custom-width="w-[calc((100%_-_var(--padding-x)_*_2)_/_2)] lg:w-[calc((100%_-_var(--padding-x)_*_2)_/_5)]"
            >
              <template #default="item">
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      xse-type="rec"
                      :xse-prod-code="product.sku"
                      :disable-swipe-mobile="true"
                      :product="{
                        ...product,
                        linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${PDP_YOU_MAY_ALSO_LIKE_ID}`,
                      }"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </template>
            </UISliderWithArrows>
          </LazyPdpRelatedProducts>
        </template>
      </XGenRecommendations>
    </DelayHydration>

    <DelayHydration>
      <CarouselTrackingProvider name="recently_viewed" partner="">
        <LazyPdpRelatedProducts
          v-if="recentlyViewedProducts?.length"
          id="recentlyViewed"
          :title="$ts('myAccount.logged.recentlyViewed.title')"
          class="!px-0 [&_>div]:!pt-12 md:[&_>div]:!py-12 xl:[&_>div]:!py-16"
        >
          <UISliderWithArrows
            :items="recentlyViewedProducts"
            :loop="false"
            has-slide-vertical-border
            slide-custom-width="w-1/2 lg:w-1/5"
          >
            <template #default="item">
              <WrapperProductTile :product="item">
                <template #default="{ product, productCardHorizontal }">
                  <UIProductTile
                    :disable-swipe-mobile="true"
                    :product="product"
                    @open-quick-buy="
                      () =>
                        productCardHorizontal &&
                        openQuickBuyPopUpLayer(productCardHorizontal)
                    "
                  />
                </template>
              </WrapperProductTile>
            </template>
          </UISliderWithArrows>
        </LazyPdpRelatedProducts>
      </CarouselTrackingProvider>
    </DelayHydration>

    <DelayHydration>
      <div
        v-show="editorialDeliveryKeys && editorialDeliveryKeys.length"
        class="editorial-modules-wrapper"
      >
        <EditorialModulesWrapper
          v-for="deliveryKey of editorialDeliveryKeys"
          :key="deliveryKey"
          :delivery-key="deliveryKey"
        />
      </div>
    </DelayHydration>

    <!-- SIDEBARS -->

    <WrapperReserveInStore
      :id-product="productId"
      :image="orderedGallery[0].data as string"
      :product="productDetailsProps"
      :selected-size-index="oneSize ? 0 : selectedSizeIndex"
      :is-single-size-by-micro-cat="props.isSingleSizeByMicroCat"
      :selected-country="selectedCountry"
      :sizes="sizes"
      :upcs="upcs"
      @update:selected-country="emit('update:selectedCountry', $event)"
    />
    <PdpInfoShippingItem />
    <PdpVirtualMirrorModal
      v-if="hasVirtualMirror"
      :get-product-virtual-mirror="getProductVirtualMirror"
      v-bind="virtualMirror"
      :product-details="productDetailsProps"
    />
    <PdpMessageSideSlide
      v-if="productDetailsProps.pdpMessage"
      v-bind="productDetailsProps.pdpMessage"
    />
    <!-- TODO: CHECK CONDITION FOR FINAL SALES SIDEBAR -->
    <PdpInfoReturnItem :sidebar-id="SIDEBAR_IDS.pdpInfoReturnItem" />
    <PdpInfoFriendsFamily
      v-if="isFriendsFamily && hideForCustomers"
      :sidebar-id="SIDEBAR_IDS.pdpFriendsFamily"
    />
    <PdpImage3dModal
      v-if="product3dImage"
      :model="product3dImage?.data?.model ?? ''"
    />
  </div>
</template>

<style lang="scss" scoped>
.swiper :deep(.swiper-pagination) {
  text-align: left;
}

.swiper :deep(.swiper-wrapper) {
  @screen lg {
    transform: none !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 0 !important;
  }
}

.swiper :deep(.swiper-slide) {
  background-color: transparent !important;
  padding-bottom: 3rem;
}

@media (min-width: 1024px) {
  .swiper :deep(.swiper-slide) {
    padding: 0 !important;
    height: 100vh !important;
    width: 100% !important;
    display: flex !important;
    align-content: center !important;
  }
}

.ctl-aspect-ration {
  aspect-ratio: auto 121 / 225;
}

.shadow {
  -webkit-box-shadow: 0 0 20px 20px #fbfbfb;
  box-shadow: 0 0 10px 10px #fbfbfb;
}

/*
* Compact the complete look section for devices with an aspect ratio below 16:9
*/
.cmtl-container {
  @screen lg {
    @media (min-aspect-ratio: 16/9) {
      max-width: 52vh;
      min-width: 380px;

      :deep(.title-price-block) {
        gap: 1.5rem;
      }
    }
  }
}

.giorgioArmaniHeaderPadding {
  :deep(.giorgioArmaniHeaderPaddingTop) {
    padding-top: var(--header-default-height);
  }
}

.breadcrumbs-desktop {
  @screen lg {
    top: max(calc(100dvh - 3rem), 600px);
    position: sticky;
    display: flex;
    max-width: 100%;
    margin-top: 24px;
  }
}

.complete-the-look-button {
  @screen lg {
    top: max(calc(100dvh - var(--header-default-height)), 400px);
    // bottom: unset;
  }
}
</style>
